import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Services from '../components/Services';
import Testimonials from '../components/Testimonials';
import Contact from '../components/Contact';
import QuoteForm from '../components/QuoteForm';
import Footer from '../components/Footer';
import CookieBanner from '../components/CookieBanner';
import { useLanguage } from '../i18n/LanguageContext';

const HomePage = () => {
  const location = useLocation();
  const { lang, seo, t, altPath } = useLanguage();

  useEffect(() => {
    const origin = window.location.origin;
    const currentUrl = lang === 'en' ? origin + '/en' : origin + '/';

    document.title = seo.home.title;
    document.documentElement.lang = lang;

    // Helper to set or create a meta tag
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.content = content;
    };

    // Primary SEO
    setMeta('name', 'description', seo.home.description);

    // Open Graph (update dynamically for current language)
    setMeta('property', 'og:title', seo.home.title);
    setMeta('property', 'og:description', seo.home.description);
    setMeta('property', 'og:url', currentUrl);
    setMeta('property', 'og:locale', lang === 'en' ? 'en_GB' : 'fr_FR');

    // Twitter Card
    setMeta('name', 'twitter:title', seo.home.title);
    setMeta('name', 'twitter:description', seo.home.description);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = currentUrl;

    // Hreflang tags (bidirectional + x-default)
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    const frUrl = origin + '/';
    const enUrl = origin + '/en';

    const addHreflang = (hreflang, href) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = hreflang;
      link.href = href;
      document.head.appendChild(link);
    };

    addHreflang('fr', frUrl);
    addHreflang('en', enUrl);
    addHreflang('x-default', frUrl);

    // Handle hash navigation
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }

    return () => {
      document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    };
  }, [location, lang, seo, altPath]);

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Header />
      <main>
        <Hero />
        <Services />
        <Testimonials />
        <Contact />
        <QuoteForm />
      </main>
      <Footer />
      <CookieBanner />

      {/* Schema.org LocalBusiness */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Reco Pneus",
          "image": "https://customer-assets.emergentagent.com/job_reco-pneus-preview/artifacts/r1jr5qsf_recopneus%20logo%20png.png",
          "telephone": "+33423340634",
          "email": "recopneus@gmail.com",
          "areaServed": ["Alpes-Maritimes", "Var", "Cannes", "Mandelieu-la-Napoule", "Antibes", "Nice", "Grasse", "Fréjus", "Draguignan", "Toulon"],
          "openingHours": "Mo-Su 09:00-19:00",
          "priceRange": "€€",
          "serviceType": t.schemaServiceTypes,
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "reviewCount": "87"
          }
        })
      }} />
    </div>
  );
};

export default HomePage;
